import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Paper, Typography, TextField, MenuItem, Button, Switch, FormControlLabel, Radio, Alert, Divider,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { ratingAPI } from '../../services/api';
import { errorText } from '../../utils/format';
import { StarRow } from './shared';

function ScaleCard({ value, current, onPick, title, text, children }) {
  const active = current === value;
  return (
    <Paper variant="outlined" onClick={() => onPick(value)}
      sx={{ p: 2, borderRadius: 2, cursor: 'pointer', borderWidth: 2, borderColor: active ? '#00796b' : '#e0e0e0', bgcolor: active ? '#e0f2f1' : '#fff', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Radio checked={active} value={value} sx={{ p: 0 }} />
        <Typography fontWeight={800}>{title}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{text}</Typography>
      {children}
    </Paper>
  );
}

export default function SettingsTab({ config, notify, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (config) setForm({
      scaleType: config.scaleType, commentMode: config.commentMode, anonymous: !!config.anonymous,
      openFrom: config.openFrom || '', openUntil: config.openUntil || '', cycleName: config.cycleName || '', instructions: config.instructions || '',
    });
  }, [config]);

  if (!form) return null;

  const save = async () => {
    setSaving(true);
    try {
      await ratingAPI.updateConfig({ ...form, openFrom: form.openFrom || '', openUntil: form.openUntil || '' });
      notify('Feedback settings saved');
      onSaved();
    } catch (e) { notify(errorText(e, 'Could not save the settings'), 'error'); }
    setSaving(false);
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 0.5 }}>How students give their rating</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        You decide the rating style. Earlier feedback is converted automatically, so old and new ratings can always be compared.
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <ScaleCard value="STARS_5" current={form.scaleType} onPick={(v) => setForm({ ...form, scaleType: v })} title="Stars (1 to 5)" text="Students tap up to five stars for each question. Quick and friendly.">
            <StarRow value={4} size={30} />
          </ScaleCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <ScaleCard value="SCORE_10" current={form.scaleType} onPick={(v) => setForm({ ...form, scaleType: v })} title="Score (1 to 10)" text="Students pick a number from 1 to 10 for each question. Finer detail for analysis.">
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                <Box key={n} sx={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700,
                  bgcolor: n === 8 ? '#00796b' : '#fff', color: n === 8 ? '#fff' : '#546e7a', border: '1px solid #b0bec5' }}>{n}</Box>
              ))}
            </Box>
          </ScaleCard>
        </Grid>
      </Grid>

      <Divider sx={{ mb: 3 }} />
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 2 }}>Comments and privacy</Typography>
            <TextField select fullWidth size="small" label="Written comment" value={form.commentMode} onChange={(e) => setForm({ ...form, commentMode: e.target.value })} sx={{ mb: 2 }}>
              <MenuItem value="OPTIONAL">Optional</MenuItem>
              <MenuItem value="REQUIRED">Required for every faculty member</MenuItem>
              <MenuItem value="HIDDEN">Do not ask for comments</MenuItem>
            </TextField>
            <FormControlLabel control={<Switch checked={form.anonymous} onChange={(e) => setForm({ ...form, anonymous: e.target.checked })} />}
              label="Anonymous feedback (hide student names from the admin screens)" />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Faculty members can never see individual feedback. With anonymous mode on, even administrators only see class-level results.
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 2 }}>Feedback window</Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <TextField fullWidth size="small" type="date" label="Opens on" InputLabelProps={{ shrink: true }} value={form.openFrom} onChange={(e) => setForm({ ...form, openFrom: e.target.value })} />
              <TextField fullWidth size="small" type="date" label="Closes on" InputLabelProps={{ shrink: true }} value={form.openUntil} onChange={(e) => setForm({ ...form, openUntil: e.target.value })} />
            </Box>
            <TextField fullWidth size="small" label="Feedback round name (optional)" placeholder="e.g. Semester 1, 2026" value={form.cycleName} onChange={(e) => setForm({ ...form, cycleName: e.target.value })} sx={{ mb: 1 }} />
            <Typography variant="caption" color="text.secondary">Leave the dates empty to keep feedback open all the time. Batches must also be switched on under Class participation.</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>Message shown to students</Typography>
            <TextField fullWidth size="small" multiline rows={2} value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })}
              placeholder="Your feedback helps us improve teaching. Only the college administration can see it." inputProps={{ maxLength: 500 }} />
          </Paper>
        </Grid>
      </Grid>

      <Alert severity="info" sx={{ my: 3 }}>Changes apply to the Student app straight away. Students who already gave feedback keep their original scores.</Alert>
      <Button variant="contained" size="large" startIcon={<SaveIcon />} onClick={save} disabled={saving}>Save settings</Button>
    </Box>
  );
}
